import {
  getScanGroup,
  type ScanGroupOptimized,
  type ScanRootOptimized,
} from '@kaiizen/kai-scan-template-system';

export type VideoToolboxClipReadStatus = 'READ' | 'NOT READ';

type AgentProvenanceScanPart = {
  agentUpdatedAt?: number;
  agentUpdateAcceptedAt?: number;
};

const hasAcceptedAgentProvenance = (part: AgentProvenanceScanPart): boolean => part.agentUpdatedAt != null && part.agentUpdateAcceptedAt != null;

const groupHasAcceptedAgentProvenance = (group: ScanGroupOptimized): boolean => {
  if (hasAcceptedAgentProvenance(group)) return true;

  for (const task of group.tasks) {
    if (hasAcceptedAgentProvenance(task)) return true;

    for (const option of task.options) {
      if (hasAcceptedAgentProvenance(option)) return true;

      for (const lineItem of option.lineItems) {
        if (hasAcceptedAgentProvenance(lineItem)) return true;
      }
    }
  }

  return false;
};

/** True when the open group has at least one scan part with accepted agent provenance. */
export const scanGroupHasAcceptedAgentUpdates = (
  scanRoot: ScanRootOptimized | undefined,
  groupId: string | undefined,
): boolean => {
  if (!scanRoot || !groupId) return false;

  const group = getScanGroup(scanRoot, groupId);
  return groupHasAcceptedAgentProvenance(group);
};

export const resolveVideoToolboxClipReadStatus = (
  scanRoot: ScanRootOptimized | undefined,
  groupId: string | undefined,
): VideoToolboxClipReadStatus => scanGroupHasAcceptedAgentUpdates(scanRoot, groupId) ? 'READ' : 'NOT READ';

/** True when any group in the scan has accepted agent provenance on a scan part. */
export const scanRootHasAcceptedAgentUpdates = (
  scanRoot: ScanRootOptimized | undefined,
): boolean => {
  if (!scanRoot) return false;

  return scanRoot.groups.some(({ id }) => scanGroupHasAcceptedAgentUpdates(scanRoot, id));
};

export const resolveVideoToolboxClipReadStatusForMediaStripVideo = (
  scanRoot: ScanRootOptimized | undefined,
  groupId: string | undefined,
): VideoToolboxClipReadStatus =>
  groupId
    ? resolveVideoToolboxClipReadStatus(scanRoot, groupId)
    : (scanRootHasAcceptedAgentUpdates(scanRoot) ? 'READ' : 'NOT READ');

export const formatVideoToolboxClipReadStatusAriaLabel = (
  readStatus: VideoToolboxClipReadStatus,
): string => (readStatus === 'READ' ? 'Read' : 'Not read');
